import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowLeft, Bell, CheckCheck, Loader2, RefreshCw } from 'lucide-react';
import { formatFullDateTime12h } from '../utils/timeFormat';

/**
 * Role-agnostic Notifications page.
 *
 * Backs the bell icon on client, admin, and agency dashboards. Talent has its
 * own `NotificationCenter` because it pulls from a different endpoint with
 * preferences + push config. This component is the lighter, read-only view
 * that everyone else needs.
 *
 * Uses `GET /api/notifications` (role-agnostic — returns booking_notifications
 * for the current user_id). Mark-read + mark-all-read available too.
 *
 * `backHref` lets each route customise the back destination without this
 * component having to know anything about the app's route structure.
 */
const Notifications = ({ backHref = '/', title = 'Notifications' }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const fetch = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get('/bookings/notifications');
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadError({
        status: err.response?.status,
        detail: err.response?.data?.detail || err.message || 'Unknown error',
      });
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const markRead = async (id) => {
    // Optimistic — flip local state first so the UI feels instant.
    setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    try {
      await api.patch(`/bookings/notifications/${id}/read`);
    } catch {
      // If it fails, quietly refetch to get server truth back.
      fetch();
    }
  };

  const markAllRead = async () => {
    const hadUnread = items.some(n => !n.is_read);
    if (!hadUnread) return;
    setItems(prev => prev.map(n => ({ ...n, is_read: true })));
    try {
      await api.post('/bookings/notifications/mark-all-read');
    } catch {
      fetch();
    }
  };

  const unreadCount = items.filter(n => !n.is_read).length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notifications-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(backHref)} data-testid="notifications-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetch} data-testid="notifications-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={markAllRead} data-testid="notifications-mark-all-read">
                <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark all read
              </Button>
            )}
          </div>
        </div>

        <h1 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <Bell className="w-6 h-6 text-cyan-600" /> {title}
          {unreadCount > 0 && (
            <Badge className="bg-cyan-600 text-white border-0 ml-1">{unreadCount}</Badge>
          )}
        </h1>

        {loadError ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-red-800">Couldn't load notifications</p>
                <p className="text-xs text-red-700 mt-0.5">
                  {loadError.status ? `HTTP ${loadError.status} · ` : ''}{loadError.detail}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={fetch} className="border-red-300 text-red-800 hover:bg-red-100">
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600">You're all caught up.</p>
              <p className="text-xs text-slate-400 mt-1">New booking activity will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {items.map(n => (
              <Card
                key={n.id}
                className={`${n.is_read ? 'bg-white' : 'bg-cyan-50 border-cyan-200'} cursor-pointer hover:border-cyan-300`}
                onClick={() => !n.is_read && markRead(n.id)}
                data-testid={`notification-${n.id}`}
              >
                <CardContent className="p-3 flex items-start gap-3">
                  <div className={`mt-1 w-2 h-2 rounded-full ${n.is_read ? 'bg-transparent' : 'bg-cyan-600'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800">{n.message}</p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {n.notification_type && <span className="uppercase tracking-wide">{n.notification_type.replace(/_/g, ' ')}</span>}
                      {n.created_at && <span> · {formatFullDateTime12h(n.created_at)}</span>}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
